// frontend/src/pages/asset-admin/AssetAdminDisposal.js
/**
 * Asset Admin - Disposal Requests Page
 * 
 * Manage asset disposal requests and approvals.
 * Theme: Red (Admin)
 */

import React, { useState, useMemo } from 'react';
import {
  Trash2, CheckCircle, XCircle, Clock, AlertCircle,
  Package, User, Calendar, MessageSquare, Laptop, Key, Car, FileKey
} from 'lucide-react';
import { useTabPersistence } from '../../hooks/usePersistence';
import Modal from '../../components/UI/Modal';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import { FilterButton } from '../../components/UI/FilterButton';

// ============================================
// CONSTANTS
// ============================================
const VALID_TABS = ['pending', 'approved', 'rejected'];

// ============================================
// CATEGORY ICON
// ============================================
const CategoryIcon = ({ code, className = "w-4 h-4" }) => {
  const icons = {
    IT: Laptop,
    ACC: Key,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// APPROVAL MODAL
// ============================================
const ApprovalModal = ({ isOpen, onClose, request, type, onSubmit, isLoading }) => {
  const [comments, setComments] = useState('');
  const isApprove = type === 'approve';
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isApprove && !comments.trim()) return;
    onSubmit({ requestId: request?.id, comments, type });
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isApprove ? 'Approve Disposal' : 'Reject Disposal'}
      size="sm"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="disposal-approval-form"
            disabled={isLoading || (!isApprove && !comments.trim())}
            className={`btn ${isApprove ? 'btn-success' : 'btn-danger'}`}
          >
            {isApprove ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {isLoading ? 'Approving...' : 'Approve'}
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                {isLoading ? 'Rejecting...' : 'Reject'}
              </>
            )}
          </button>
        </>
      }
    >
      <form id="disposal-approval-form" onSubmit={handleSubmit} className="space-y-4">
              <div className={`rounded-lg p-3 ${isApprove ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={`text-sm font-medium ${isApprove ? 'text-green-800' : 'text-red-800'}`}>
                  {request?.asset_tag}
                </p>
                <p className={`text-xs ${isApprove ? 'text-green-600' : 'text-red-600'}`}>
                  {request?.asset_type_name}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Disposal Reason:</p>
                <p className="text-sm text-gray-700">{request?.reason}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isApprove ? 'Comments (optional)' : 'Rejection Reason'}
                  {!isApprove && <span className="text-red-500"> *</span>}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={isApprove ? 'Add any notes...' : 'Explain why this request is rejected...'}
                  className="input-admin w-full h-24 resize-none"
                  required={!isApprove}
                />
              </div>
      </form>
    </Modal>
  );
};

// ============================================
// DISPOSAL CARD
// ============================================
const DisposalCard = ({ request, onApprove, onReject }) => {
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900">{request.asset_tag}</p>
            <p className="text-xs text-gray-500">{request.asset_type_name}</p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={request.status} label={request.status === 'pending' ? 'Pending Approval' : undefined} />
        </div>
      </div>

      <div className="space-y-3 flex-1">
      {/* Reason */}
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-xs text-gray-500 mb-1">Reason for disposal:</p>
        <p className="text-sm text-gray-700 line-clamp-2">{request.reason}</p>
      </div>

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {request.requested_by_name}
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {new Date(request.created_at).toLocaleDateString()}
        </div>
      </div>

      {/* Proposed Method */}
      {request.proposed_method && (
        <p className="text-xs text-gray-500">
          Method: <span className="text-gray-700">{request.proposed_method}</span>
        </p>
      )}
      </div>
      
      {/* Actions */}
      {request.status === 'pending' && (
        <div className="card-action-footer">
          <button 
            onClick={() => onReject(request)}
            className="flex-1 btn btn-sm btn-admin-outline flex gap-1"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>
          <button 
            onClick={() => onApprove(request)}
            className="flex-1 btn btn-sm btn-success flex gap-1"
          >
            <CheckCircle className="w-4 h-4" />
            Approve
          </button>
        </div>
      )}
      
      {/* Approval Info */}
      {request.status === 'approved' && request.approved_by_name && (
        <div className="pt-3 border-t text-xs text-green-600">
          <CheckCircle className="w-3 h-3 inline mr-1" />
          Approved by {request.approved_by_name} on {new Date(request.approval_date).toLocaleDateString()}
        </div>
      )}
      
      {/* Rejection Info */}
      {request.status === 'rejected' && request.rejection_reason && (
        <div className="pt-3 border-t">
          <p className="text-xs text-red-600 mb-1">
            <XCircle className="w-3 h-3 inline mr-1" />
            Rejected
          </p>
          <p className="text-xs text-gray-500">{request.rejection_reason}</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const AssetAdminDisposal = () => {
  const [activeTab, setActiveTab] = useTabPersistence('asset-admin-disposal', 'pending', VALID_TABS);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal states
  const [approvalModal, setApprovalModal] = useState({ open: false, request: null, type: null });
  
  // Mock data - replace with actual API call
  const [disposalRequests] = useState([
    {
      id: 1,
      asset_tag: 'IT-LP-0045',
      asset_type_name: 'Laptop',
      category_code: 'IT',
      reason: 'Screen damage beyond repair. Display cracked and not responsive. Cost of repair exceeds replacement value.',
      proposed_method: 'E-waste recycling',
      requested_by_name: 'John Doe',
      requested_by_staff_id: 'EMP001',
      status: 'pending',
      created_at: '2024-12-15T10:30:00Z',
    },
    {
      id: 2,
      asset_tag: 'IT-LP-0032',
      asset_type_name: 'Laptop',
      category_code: 'IT',
      reason: 'End of life - 7 years old, performance issues, no longer supported.',
      proposed_method: 'Donation to charity',
      requested_by_name: 'Jane Smith',
      requested_by_staff_id: 'EMP002',
      status: 'pending',
      created_at: '2024-12-14T14:20:00Z',
    },
    {
      id: 3,
      asset_tag: 'ACC-KY-0012',
      asset_type_name: 'Access Card',
      category_code: 'ACC',
      reason: 'Card deactivated and no longer functional.',
      proposed_method: 'Physical destruction',
      requested_by_name: 'Mike Johnson',
      requested_by_staff_id: 'EMP003',
      status: 'approved',
      approved_by_name: 'Admin User',
      approval_date: '2024-12-10T09:00:00Z',
      created_at: '2024-12-08T11:00:00Z',
    },
  ]);
  
  // Filter requests
  const filteredRequests = useMemo(() => {
    let filtered = disposalRequests.filter(r => r.status === activeTab);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.asset_tag?.toLowerCase().includes(query) ||
        r.requested_by_name?.toLowerCase().includes(query) ||
        r.reason?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [disposalRequests, activeTab, searchQuery]);
  
  // Tab definitions with counts
  const tabs = [
    { id: 'pending', name: 'Pending', icon: Clock, count: disposalRequests.filter(r => r.status === 'pending').length },
    { id: 'approved', name: 'Approved', icon: CheckCircle, count: disposalRequests.filter(r => r.status === 'approved').length },
    { id: 'rejected', name: 'Rejected', icon: XCircle, count: disposalRequests.filter(r => r.status === 'rejected').length },
  ];
  
  // Handlers
  const handleApprove = (request) => {
    setApprovalModal({ open: true, request, type: 'approve' });
  };
  
  const handleReject = (request) => {
    setApprovalModal({ open: true, request, type: 'reject' });
  };
  
  const handleSubmitApproval = ({ requestId, comments, type }) => {
    // TODO: Call API
    console.log('Submitting:', { requestId, comments, type });
    setApprovalModal({ open: false, request: null, type: null });
  };

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Disposal Requests</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">Review and approve asset disposal requests</p>
            </div>
          </div>
          
          {/* Pending Count */}
          {tabs[0].count > 0 && (
            <div className="px-4 py-2 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-700">
                  {tabs[0].count} pending approval{tabs[0].count > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
              {tab.count > 0 && (
                <CountBadge count={tab.count} theme="admin" active={isActive} />
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center justify-between gap-3">
          <DebouncedSearchInput
            value={searchQuery}
            onSearch={setSearchQuery}
            placeholder="Search by asset tag, requester, or reason..."
            className="max-w-xl"
          />
          <div className="flex items-center gap-2">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={0}
              onClick={() => setShowFilters(v => !v)}
            />
          </div>
        </div>
      </div>

      {/* Disposal Requests Grid */}
      {filteredRequests.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Trash2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No {activeTab} disposal requests</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRequests.map(request => (
            <DisposalCard 
              key={request.id} 
              request={request} 
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}

      {/* Approval Modal */}
      <ApprovalModal
        isOpen={approvalModal.open}
        onClose={() => setApprovalModal({ open: false, request: null, type: null })}
        request={approvalModal.request}
        type={approvalModal.type}
        onSubmit={handleSubmitApproval}
        isLoading={false}
      />
    </div>
  );
};

export default AssetAdminDisposal;
